<template>
  <div class="card card-custom gutter-b mt-4">
    <div class="card-header border-0 pt-6">
      <div class="card-title">
        <div class="d-flex flex-column">
          <h3 class="card-label font-weight-bolder text-dark mb-1">
            ตั้งค่าการเปิด-ปิดข้อมูลการสมัครสมาชิก
          </h3>
        </div>
      </div>
    </div>

    <div class="card-body pb-6 pt-2">
      <div v-if="isLoading" class="text-center py-10">
        <div class="spinner spinner-primary spinner-lg mr-15"></div>
        <span class="text-muted font-weight-bold mt-2 d-block">
          กำลังโหลดข้อมูลการตั้งค่า...
        </span>
      </div>

      <div v-else class="table-responsive">
        <table
          class="table table-head-custom table-vertical-center table-hover"
        >
          <thead>
            <tr class="text-uppercase text-secondary">
              <th style="width: 60px" class="text-center">#</th>
              <th style="min-width: 200px">ข้อมูลฟิลด์</th>
              <th style="min-width: 140px" class="text-center">ข้อกำหนด</th>
              <th style="min-width: 160px" class="text-center">
                การเปิดใช้งาน
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in fieldsConfig"
              :key="item.key"
              :class="{ 'bg-light-secondary': item.required }"
            >
              <td class="text-center font-weight-bold text-muted">
                {{ index + 1 }}
              </td>

              <td>
                <span class="font-weight-bolder text-dark font-size-lg">
                  {{ item.label }}
                </span>
              </td>

              <td class="text-center">
                <span
                  class="badge badge-light-secondary text-dark font-weight-bold py-2 px-3"
                >
                  {{ item.required ? "บังคับ" : "ไม่บังคับ" }}
                </span>
              </td>

              <td class="text-center">
                <div
                  class="d-inline-flex align-items-center justify-content-center pl-11"
                >
                  <span
                    class="switch switch-sm"
                    :class="item.required ? 'switch-danger' : 'switch-primary'"
                  >
                    <label class="mb-0">
                      <input
                        type="checkbox"
                        v-model="item.enabled"
                        :disabled="item.required"
                      />
                      <span></span>
                    </label>
                  </span>
                  <span
                    class="ml-3 font-weight-bolder font-size-sm"
                    :class="item.enabled ? 'text-primary' : 'text-muted'"
                    style="width: 40px; text-align: left"
                  >
                    {{ item.enabled ? "เปิด" : "ปิด" }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="!isLoading"
        class="d-flex justify-content-end align-items-center mt-6 pt-4 border-top"
      >
        <div>
          <b-button
            variant="primary"
            :disabled="isLoading || isSaving"
            @click="saveSettings()"
          >
            <span
              v-if="isSaving"
              class="spinner-border spinner-border-sm mr-1"
              role="status"
            ></span>
            บันทึกการตั้งค่า
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import {
  GET_COMPANY_REGISTRATION_SETTING,
  UPDATE_COMPANY_REGISTRATION_SETTING
} from "@/core/services/store/company_registration_setting.module";

export default {
  name: "CompanyRegistrationSetting",
  props: {
    companyId: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      fieldsConfig: [
        {
          key: "first_name",
          label: "ชื่อ",
          required: false,
          enabled: true
        },
        {
          key: "last_name",
          label: "นามสกุล",
          required: false,
          enabled: true
        },
        {
          key: "phone_number",
          label: "เบอร์โทรศัพท์",
          required: false,
          enabled: true
        },
        {
          key: "gender",
          label: "เพศ",
          required: false,
          enabled: true
        },
        {
          key: "birthdate",
          label: "วันเดือนปีเกิด",
          required: false,
          enabled: true
        },
        {
          key: "age_range",
          label: "ช่วงอายุ",
          required: false,
          enabled: false
        },
        {
          key: "address",
          label: "ที่อยู่",
          required: false,
          enabled: true
        },
        {
          key: "referral_code",
          label: "รหัสผู้แนะนำเพื่อน",
          required: false,
          enabled: false
        }
      ]
    };
  },
  watch: {
    companyId(newVal) {
      if (newVal > 0) {
        this.loadSettings();
      }
    }
  },
  created() {
    if (this.companyId > 0) {
      this.loadSettings();
    }
  },
  methods: {
    async loadSettings() {
      if (!this.companyId || Number(this.companyId) <= 0) return;

      this.isLoading = true;
      try {
        const res = await this.$store.dispatch(
          GET_COMPANY_REGISTRATION_SETTING,
          { company_id: this.companyId }
        );

        const resData = res && res.data ? res.data : res;
        const data = resData.data ? resData.data : resData;
        if (data) {
          const requiredList = Array.isArray(data.required_fields)
            ? data.required_fields
            : ["first_name", "last_name", "phone_number", "gender"];

          this.fieldsConfig.forEach((item) => {
            const isRequired = requiredList.includes(item.key);
            item.required = isRequired;
            if (isRequired) {
              item.enabled = true;
            } else if (typeof data[item.key] !== "undefined") {
              item.enabled = Number(data[item.key]) === 1;
            }
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load registration setting:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async saveSettings() {
      if (!this.companyId || Number(this.companyId) <= 0) return;

      this.isSaving = true;

      const payload = {};
      this.fieldsConfig.forEach((f) => {
        payload[f.key] = f.required ? 1 : f.enabled ? 1 : 0;
      });

      try {
        const res = await this.$store.dispatch(
          UPDATE_COMPANY_REGISTRATION_SETTING,
          {
            company_id: this.companyId,
            data: payload
          }
        );

        const resData = res && res.data ? res.data : res;
        if (resData.status === "success" || resData.data) {
          Swal.fire({
            icon: "success",
            title: "บันทึกการตั้งค่าสำเร็จ!",
            text: "การตั้งค่าเปิด-ปิดฟิลด์สมัครสมาชิกถูกบันทึกเรียบร้อยแล้ว",
            confirmButtonText: "ตกลง",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text:
              resData.error ||
              "ไม่สามารถบันทึกการตั้งค่าได้ กรุณาลองใหม่อีกครั้ง",
            confirmButtonText: "ตกลง",
            customClass: {
              confirmButton: "btn btn-danger"
            }
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to save registration setting:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถบันทึกการตั้งค่าได้ กรุณาลองใหม่อีกครั้ง",
          confirmButtonText: "ตกลง",
          customClass: {
            confirmButton: "btn btn-danger"
          }
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
}

.switch input:not(:checked) ~ span:before {
  background-color: #e4e6ef !important;
}

.switch input:not(:checked) ~ span:after {
  background-color: #ffffff !important;
  opacity: 1 !important;
}
</style>
